/**
 * E2EE P2P Chat Hook with End-to-End Encryption
 * 
 * Combines WebRTC for direct P2P + E2EE for message encryption
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import Peer, { Instance as SimplePeerInstance } from 'simple-peer';
import { useChatStore } from '../store';

const DISPATCHER_URL = import.meta.env.VITE_DISPATCHER_URL || 'ws://localhost:8080';

// E2EE types (same as in p2p-chat package)
interface KeyPair {
  publicKey: CryptoKey;
  privateKey: CryptoKey;
}

interface ExportedPublicKey {
  type: 'e2ee-pubkey';
  key: string;
}

interface EncryptedMessage {
  type: 'encrypted';
  iv: string;
  ciphertext: string;
}

// E2EE functions (inline for browser compatibility)
async function generateKeyPair(): Promise<KeyPair> {
  const keyPair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey', 'deriveBits']
  );
  return { publicKey: keyPair.publicKey, privateKey: keyPair.privateKey };
}

async function exportPublicKey(publicKey: CryptoKey): Promise<ExportedPublicKey> {
  const exported = await crypto.subtle.exportKey('raw', publicKey);
  const base64 = btoa(String.fromCharCode(...new Uint8Array(exported)));
  return { type: 'e2ee-pubkey', key: base64 };
}

async function importPublicKey(exported: ExportedPublicKey): Promise<CryptoKey> {
  const binary = Uint8Array.from(atob(exported.key), c => c.charCodeAt(0));
  return crypto.subtle.importKey('raw', binary, { name: 'ECDH', namedCurve: 'P-256' }, true, []);
}

async function deriveSharedKey(privateKey: CryptoKey, peerPublicKey: CryptoKey): Promise<CryptoKey> {
  const sharedBits = await crypto.subtle.deriveBits({ name: 'ECDH', public: peerPublicKey }, privateKey, 256);
  const hkdfKey = await crypto.subtle.importKey('raw', sharedBits, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new TextEncoder().encode('OMNI-GRID-P2P-E2EE-v1'), info: new TextEncoder().encode('chat-encryption') },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function encryptMessage(sharedKey: CryptoKey, plaintext: string): Promise<EncryptedMessage> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, tagLength: 128 }, sharedKey, encoded);
  return {
    type: 'encrypted',
    iv: btoa(String.fromCharCode(...iv)),
    ciphertext: btoa(String.fromCharCode(...new Uint8Array(ciphertext))),
  };
}

async function decryptMessage(sharedKey: CryptoKey, encrypted: EncryptedMessage): Promise<string> {
  const iv = Uint8Array.from(atob(encrypted.iv), c => c.charCodeAt(0));
  const ciphertext = Uint8Array.from(atob(encrypted.ciphertext), c => c.charCodeAt(0));
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv, tagLength: 128 }, sharedKey, ciphertext);
  return new TextDecoder().decode(decrypted);
}

// Hook options
interface UseE2EEChatOptions {
  token: string;
  roomId: string;
  enableE2EE?: boolean;
}

export function useE2EEChat({ token, roomId, enableE2EE = true }: UseE2EEChatOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const peerRef = useRef<SimplePeerInstance | null>(null);
  
  // E2EE state
  const [keyPair, setKeyPair] = useState<KeyPair | null>(null);
  const [sharedKey, setSharedKey] = useState<CryptoKey | null>(null);
  const [fingerprint, setFingerprint] = useState<string>('');
  const [e2eeReady, setE2EEReady] = useState(false);
  
  const [isInitiator, setIsInitiator] = useState(false);
  const [iceServers, setIceServers] = useState<any[]>([
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]);
  
  const { addMessage, setConnected, setPeerConnected, setRoomStatus } = useChatStore();

  // Initialize E2EE keys
  useEffect(() => {
    if (enableE2EE) {
      generateKeyPair().then(kp => {
        setKeyPair(kp);
        console.log('[E2EE] Key pair generated');
      });
    }
  }, [enableE2EE]);

  // Initialize WebSocket connection
  useEffect(() => {
    const ws = new WebSocket(DISPATCHER_URL);
    wsRef.current = ws;

    ws.onopen = async () => {
      console.log('[P2P] WebSocket connected, joining room...');
      ws.send(JSON.stringify({ type: 'join', token }));
      
      // Request ICE servers with TURN credentials
      ws.send(JSON.stringify({ type: 'get-ice-servers' }));
      
      // Send public key for E2EE handshake
      if (keyPair && enableE2EE) {
        const exported = await exportPublicKey(keyPair.publicKey);
        ws.send(JSON.stringify({ type: 'e2ee-handshake', publicKey: exported }));
        console.log('[E2EE] Public key sent');
      }
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      handleServerMessage(message);
    };

    ws.onclose = () => {
      console.log('[P2P] WebSocket disconnected');
      setConnected(false);
    };

    ws.onerror = (err) => {
      console.error('[P2P] WebSocket error:', err);
    };

    return () => {
      ws.close();
      peerRef.current?.destroy();
    };
  }, [token, roomId, keyPair, enableE2EE]);

  const handleServerMessage = useCallback(async (message: any) => {
    switch (message.type) {
      case 'ice-servers':
        // Update ICE servers with TURN credentials from server
        if (message.servers && Array.isArray(message.servers)) {
          setIceServers(message.servers);
          console.log('[P2P] Received ICE servers:', message.servers.length);
        }
        break;

      case 'room-update':
        setRoomStatus(message.room.status);
        setPeerConnected(message.room.makerConnected && message.room.takerConnected);
        
        if (message.room.makerConnected && message.room.takerConnected && !peerRef.current) {
          if (!isInitiator) {
            setIsInitiator(true);
            initWebRTC(true);
          }
        }
        setConnected(true);
        break;

      case 'e2ee-handshake':
        // Receive peer's public key
        if (keyPair && message.publicKey && enableE2EE) {
          try {
            const peerPubKey = await importPublicKey(message.publicKey);
            const derived = await deriveSharedKey(keyPair.privateKey, peerPubKey);
            setSharedKey(derived);
            setE2EEReady(true);
            
            // Generate fingerprint
            const myExported = await exportPublicKey(keyPair.publicKey);
            const combined = myExported.key + message.publicKey.key;
            const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(combined));
            const fp = Array.from(new Uint8Array(hash).slice(0, 4))
              .map(b => b.toString(16).padStart(2, '0'))
              .join(':').toUpperCase();
            setFingerprint(fp);
            
            console.log('[E2EE] Handshake complete! Fingerprint:', fp);
          } catch (err) {
            console.error('[E2EE] Handshake failed:', err);
          }
        }
        break;

      case 'peer-signal':
        if (!peerRef.current) {
          initWebRTC(false);
        }
        peerRef.current?.signal(message.data);
        break;

      case 'peer-message':
        // Handle encrypted or plain message
        if (message.encrypted && sharedKey) {
          try {
            const decrypted = await decryptMessage(sharedKey, message.encryptedData);
            addMessage({ content: decrypted, from: 'peer', encrypted: true });
          } catch (err) {
            console.error('[E2EE] Decryption failed:', err);
            addMessage({ content: '[Encrypted message - decryption failed]', from: 'peer', encrypted: true });
          }
        } else {
          addMessage({ content: message.content, from: 'peer', encrypted: false });
        }
        break;

      case 'error':
        console.error('[P2P] Server error:', message.code, message.message);
        break;
    }
  }, [isInitiator, keyPair, sharedKey, enableE2EE]);

  const initWebRTC = useCallback((initiator: boolean) => {
    console.log(`[P2P] Initializing WebRTC as ${initiator ? 'initiator' : 'receiver'}`);
    
    const peer = new Peer({
      initiator,
      trickle: true,
      config: {
        iceServers: iceServers,
      },
    });

    peer.on('signal', (data) => {
      wsRef.current?.send(JSON.stringify({ type: 'signal', data }));
    });

    peer.on('connect', () => {
      console.log('[P2P] WebRTC connected!');
      setPeerConnected(true);
    });

    peer.on('data', async (data) => {
      const message = JSON.parse(data.toString());
      
      if (message.encrypted && sharedKey) {
        try {
          const decrypted = await decryptMessage(sharedKey, message.encryptedData);
          addMessage({ content: decrypted, from: 'peer', encrypted: true });
        } catch (err) {
          console.error('[E2EE] Decryption failed:', err);
        }
      } else {
        addMessage({ content: message.content, from: 'peer', encrypted: false });
      }
    });

    peer.on('close', () => {
      console.log('[P2P] WebRTC disconnected');
      setPeerConnected(false);
    });

    peer.on('error', (err) => {
      console.error('[P2P] WebRTC error:', err);
    });

    peerRef.current = peer;
  }, [sharedKey, iceServers]);

  const sendMessage = useCallback(async (content: string) => {
    let message: any;
    
    if (e2eeReady && sharedKey) {
      // Encrypt message
      const encryptedData = await encryptMessage(sharedKey, content);
      message = { encrypted: true, encryptedData, timestamp: Date.now() };
    } else {
      // Send plain (warn user)
      message = { content, encrypted: false, timestamp: Date.now() };
    }
    
    // Try WebRTC first
    if (peerRef.current?.connected) {
      peerRef.current.send(JSON.stringify(message));
    } else {
      // Fallback to WebSocket relay
      wsRef.current?.send(JSON.stringify({ type: 'message', ...message }));
    }
    
    addMessage({ content, from: 'me', encrypted: e2eeReady });
  }, [e2eeReady, sharedKey]);

  const sendTradeAction = useCallback((action: 'confirm-payment' | 'release-crypto' | 'dispute', proof?: string) => {
    wsRef.current?.send(JSON.stringify({ type: 'trade-action', action, proof }));
  }, []);

  return {
    sendMessage,
    sendTradeAction,
    e2eeReady,
    fingerprint,
  };
}
